import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Navigation } from './components/navigation/Navigation';
import { AdminSidebar } from './components/navigation/AdminSidebar';
import { UserSidebar } from './components/navigation/UserSidebar';
import { Homepage } from './pages/Homepage';
import { AdminFarmersDirectory } from './pages/Admin/AdminFarmersDirectory';
import { UserFarmersDirectory } from './pages/User/UserFarmersDirectory';
import { FarmersAccountDirectory } from './pages/Farmer/FarmersAccountDirectory';
import { AdminProcessorsDirectory } from './pages/Admin/AdminProcessorsDirectory';
import { UserProcessorsDirectory } from './pages/User/UserProcessorsDirectory';
import { ProcessorsAccountDirectory } from './pages/Processors/ProcessorsAccountDirectory';
import { Analytics } from './pages/Analytics';
import { About } from './pages/About';
import { Maps } from './pages/Maps';
import { AdminMap } from './pages/Admin/adminMap';
import { LoginForm } from './components/forms/LoginForm';
import { SignUpForm } from './components/forms/SignUpForm';
import { AdminApplicationForm } from './components/forms/AdminApplicationForm';
import { WelcomePage } from './pages/WelcomePage';
import { UserProfile } from './pages/User/UserProfile';
import { AdminProfile } from './pages/Admin/AdminProfile';
import Services from './pages/Services';
import AdminServices from './pages/Admin/AdminServices';
import { Research } from './pages/Research';
import { PendingApplications } from './pages/Admin/PendingApplications';
import { PendingProfileRequests } from './pages/Admin/PendingProfileRequests';
import { MessagesPage } from './pages/MessagesPage';
import { AdminConcernsDashboard } from './pages/Admin/AdminConcernsDashboard';
import { AnnouncementsPage } from './pages/AnnouncementsPage';
import { Settings } from './pages/Settings';
import { NotFound } from './pages/NotFound';
import { TopHeaderBar } from './components/navigation/TopHeaderBar';
import { ProtectedRoute } from './components/shared/ProtectedRoute';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { authAPI, adminAPI } from './services/api';

export default function App() {
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showAdminApplication, setShowAdminApplication] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Load user from sessionStorage on app initialization
  useEffect(() => {
    try {
      const savedUser = sessionStorage.getItem('bciis_user');
      if (savedUser) {
        const userData = JSON.parse(savedUser);
        setUser(userData);
      }
    } catch (error) {
      sessionStorage.removeItem('bciis_user');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleLogin = (userData) => {
    const savedUser = sessionStorage.getItem('bciis_user');
    let mergedUser = userData;
    if (savedUser) {
      try {
        const existingUser = JSON.parse(savedUser);
        if (!userData.avatar && existingUser.avatar && existingUser.username === userData.username) {
          mergedUser = { ...userData, avatar: existingUser.avatar };
        }
      } catch (error) { /* ignore */ }
    }
    setUser(mergedUser);
    sessionStorage.setItem('bciis_user', JSON.stringify(mergedUser));
    setShowLogin(false);
  };

  const handleSignUp = (userData) => {
    setUser(userData);
    sessionStorage.setItem('bciis_user', JSON.stringify(userData));
    setShowSignUp(false);
  };

  const handleLogout = () => {
    setUser(null);
    sessionStorage.removeItem('bciis_user');
    sessionStorage.removeItem('bciis_auth_token');
  };

  const switchToSignUp = () => {
    setShowLogin(false);
    setShowSignUp(true);
  };

  const switchToLogin = () => {
    setShowSignUp(false);
    setShowLogin(true);
  };

  const switchToAdminApplication = () => {
    setShowLogin(false);
    setShowSignUp(false);
    setShowAdminApplication(true);
  };

  const handleUpdateUser = async (updatedUserData) => {
    try {
      const mergedUserData = { ...user, ...updatedUserData };
      setUser(mergedUserData);
      sessionStorage.setItem('bciis_user', JSON.stringify(mergedUserData));

      if (user.role === 'admin') {
        await adminAPI.updateProfile(user.id, updatedUserData);
      } else {
        const response = await authAPI.updateProfile(updatedUserData);
        if (response.success && response.user) {
          setUser(response.user);
          sessionStorage.setItem('bciis_user', JSON.stringify(response.user));
        }
      }
    } catch (error) {
      // Local update already applied
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-green-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Layout flags
  const isAdmin = user?.role === 'admin';
  const isLoggedIn = !!user;

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <div className={`min-h-screen bg-gradient-to-br from-amber-50 to-green-50 ${
          isLoggedIn ? 'flex' : ''
        }`}>
          {/* Sidebar for admin, UserSidebar for regular users, top navbar for guests */}
          {isAdmin ? (
            <AdminSidebar user={user} onLogout={handleLogout} onCollapseChange={setSidebarCollapsed} />
          ) : isLoggedIn ? (
            <UserSidebar user={user} onLogout={handleLogout} onCollapseChange={setSidebarCollapsed} />
          ) : (
            <Navigation
              user={user}
              onLogout={handleLogout}
              onLogin={() => setShowLogin(true)}
              onSignUp={() => setShowSignUp(true)}
            />
          )}

          {/* Main content area — margin reacts to sidebar collapse state */}
          <main className={isLoggedIn
            ? `flex-1 min-h-screen pt-14 md:pt-0 transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'}`
            : 'pt-20'}>
            {isLoggedIn && <TopHeaderBar user={user} sidebarCollapsed={sidebarCollapsed} />}
            <Routes>
              {/* Public: Welcome page (when not logged in) or redirect to /home */}
              <Route
                path="/"
                element={
                  user
                    ? <Navigate to="/home" replace />
                    : <WelcomePage onLogin={() => setShowLogin(true)} onSignUp={() => setShowSignUp(true)} />
                }
              />

              {/* Protected routes */}
              <Route
                path="/home"
                element={
                  <ProtectedRoute user={user}>
                    <Homepage user={user} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/announcements"
                element={
                  <ProtectedRoute user={user}>
                    <AnnouncementsPage user={user} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/farmers"
                element={
                  <ProtectedRoute user={user}>
                    {user?.role === 'admin' ? (
                      <AdminFarmersDirectory />
                    ) : (user?.role === 'farmer' || user?.role === 'processor') ? (
                      <FarmersAccountDirectory user={user} />
                    ) : (
                      <UserFarmersDirectory user={user} />
                    )}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/processors"
                element={
                  <ProtectedRoute user={user}>
                    {user?.role === 'admin' ? (
                      <AdminProcessorsDirectory />
                    ) : (user?.role === 'farmer' || user?.role === 'processor') ? (
                      <ProcessorsAccountDirectory user={user} />
                    ) : (
                      <UserProcessorsDirectory user={user} />
                    )}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/map"
                element={
                  <ProtectedRoute user={user}>
                    {user?.role === 'admin' ? <AdminMap /> : <Maps />}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/services"
                element={
                  <ProtectedRoute user={user}>
                    {user?.role === 'admin' ? <AdminServices /> : <Services />}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/research"
                element={
                  <ProtectedRoute user={user}>
                    <Research user={user} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute user={user}>
                    <Analytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/about"
                element={
                  <ProtectedRoute user={user}>
                    <About />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute user={user}>
                    {user?.role === 'admin' ? (
                      <AdminProfile
                        user={user}
                        onUpdateUser={handleUpdateUser}
                      />
                    ) : (
                      <UserProfile
                        user={user}
                        onUpdateUser={handleUpdateUser}
                      />
                    )}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute user={user}>
                    <Settings user={user} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/applications"
                element={
                  <ProtectedRoute user={user} adminOnly>
                    <PendingApplications user={user} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/profile-requests"
                element={
                  <ProtectedRoute user={user} adminOnly>
                    <PendingProfileRequests user={user} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/messages"
                element={
                  <ProtectedRoute user={user}>
                    {['admin', 'farmer', 'processor'].includes(user?.role) ? (
                      <MessagesPage user={user} />
                    ) : (
                      <Navigate to="/home" replace />
                    )}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/concerns"
                element={
                  <ProtectedRoute user={user} adminOnly>
                    <AdminConcernsDashboard user={user} />
                  </ProtectedRoute>
                }
              />

              {/* 404 catch-all */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>

          {/* Modal overlays */}
          {showLogin && (
            <LoginForm
              onClose={() => setShowLogin(false)}
              onLogin={handleLogin}
              onSwitchToSignUp={switchToSignUp}
              onSwitchToAdminApplication={switchToAdminApplication}
            />
          )}

          {showSignUp && (
            <SignUpForm
              onClose={() => setShowSignUp(false)}
              onSignUp={handleSignUp}
              onSwitchToLogin={switchToLogin}
            />
          )}

          {showAdminApplication && (
            <AdminApplicationForm
              onClose={() => setShowAdminApplication(false)}
              onSuccess={() => setShowAdminApplication(false)}
            />
          )}
        </div>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
